import { supabase } from "@/lib/supabase";

export interface Member {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  joined_at: string;
  type: "member";
}

export interface Invitation {
  id: string;
  email: string;
  status: string;
  created_at: string;
  expires_at: string;
  invited_by_email?: string;
  invited_by_name?: string;
  type: "invitation";
}

export interface MembersListParams {
  organizationId: string;
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface MembersListResponse {
  success: boolean;
  members: Member[];
  invitations: Invitation[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface InviteMemberData {
  organizationId: string;
  email: string;
}

/**
 * Admin Members Service
 * Manages organization members and invitations via edge function
 * Following TanStack Query patterns from CLAUDE.md
 */
export const adminMembersService = {
  /**
   * Get paginated list of members and invitations for an organization
   * Uses edge function with server-side pagination
   */
  async getMembers(params: MembersListParams): Promise<MembersListResponse> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "listMembers",
          ...params,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch members");
    }

    return data;
  },

  /**
   * Invite a new member to the organization
   * Uses the same invite-team-member function to send emails
   */
  async inviteMember(
    inviteData: InviteMemberData,
  ): Promise<{ success: boolean; data: Invitation }> {
    // Get current user info for the invitation
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("Not authenticated");
    }

    // Get user profile for inviter info
    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name, email")
      .eq("id", user.id)
      .single();

    // Get organization name
    const { data: org } = await supabase
      .from("organizations")
      .select("name")
      .eq("id", inviteData.organizationId)
      .single();

    // Use the same invite-team-member edge function that sends emails
    const { data, error } = await supabase.functions.invoke(
      "invite-team-member",
      {
        body: {
          email: inviteData.email,
          organizationId: inviteData.organizationId,
          organizationName: org?.name || "Organization",
          inviterName: profile?.full_name || profile?.email || "Admin",
          inviterEmail: profile?.email || user.email || "",
          userId: user.id,
          siteUrl: window.location.origin,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to invite member");
    }

    return data;
  },

  /**
   * Remove a member from the organization
   * Clears the organization_id from their profile
   * @param deleteAuthUser - If true, also deletes the user's auth account (use with caution)
   */
  async removeMember(
    organizationId: string,
    memberId: string,
    deleteAuthUser: boolean = false,
  ): Promise<{ success: boolean; message: string; warning?: string }> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "removeMember",
          organizationId,
          memberId,
          deleteAuthUser,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to remove member");
    }

    return data;
  },

  /**
   * Cancel a pending invitation
   * Updates invitation status to 'cancelled'
   */
  async cancelInvitation(
    organizationId: string,
    invitationId: string,
  ): Promise<{ success: boolean; message: string }> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "cancelInvitation",
          organizationId,
          invitationId,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to cancel invitation");
    }

    return data;
  },
};
